import { useState, useEffect } from "react"
import { Gauge } from "~/react/ui/health-gauge"
import { Icon } from "~/react/ui/icon"
import { Count } from "~/react/ui/count"
import { Hint } from "~/react/ui/hint"
import { Skeleton } from "~/react/ui/skeleton"
import { Button } from "@/react/ui/button"
import { checkout_upgrade_plan_path } from "~/lib/routes"
import emptyStateImageUrl from "~/images/empty_states/health-check.svg"
import { IssueItem } from "~/react/components/issue_item"

type Issue = {
  severity: string
  description: string
  category: string
  title?: string
  subtitle?: string
  criticality?: string
  error_key?: string
  actions?: Array<{ label?: string; href?: string }>
}

type CheckItem = {
  key?: string
  description: string | null
  severity: string | null
  passed: boolean
}

type CategoryData = {
  name: string
  passed: boolean
  issue_count: number
  items: CheckItem[]
}

type CheckGroup = {
  key: string
  label_key: string
  categories: CategoryData[]
  summary: string
  status: string
  passed_count: number
  failed_count: number
}

type HealthReport = {
  score: number | null
  status: "excellent" | "healthy" | "at-risk" | "critical" | null
  last_checked_at: number | string | null
  issues: Issue[]
  checks: Record<string, { status: string; issue_count: number; items?: CheckItem[] }>
  check_groups?: CheckGroup[]
}

type Props = {
  domain: string
  health: number | null
  isPremium?: boolean
}

export default function HealthTab({ domain, health, isPremium = false }: Props) {
  const [healthReport, setHealthReport] = useState<HealthReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchHealthReport = async (showLoading = true) => {
    if (!isPremium) {
      setIsLoading(false)
      return
    }
    if (showLoading) setIsLoading(true)
    try {
      const response = await fetch(`/email-domains/${encodeURIComponent(domain)}/health-report.json`, {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
      if (response.ok) {
        const data = await response.json()
        setHealthReport(data)
      }
    } catch (error) {
      console.error("Failed to fetch health report:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Initial fetch and refetch when health changes (from ActionCable update)
  useEffect(() => {
    fetchHealthReport()
  }, [domain, health, isPremium])

  const getHealthLabel = (score: number): string => {
    if (score >= 90) return "excellent"
    if (score >= 70) return "healthy"
    if (score >= 50) return "at-risk"
    return "critical"
  }

  const getStatusModifier = () => {
    if (!healthReport?.status) return ""
    switch (healthReport.status) {
      case "critical":
        return "health-report-tab--critical"
      case "at-risk":
        return "health-report-tab--at-risk"
      case "healthy":
        return "health-report-tab--healthy"
      case "excellent":
        return "health-report-tab--excellent"
    }
  }

  const getStatusMessage = () => {
    if (!healthReport?.status) return ""
    return i18n.t(`js.email_accounts_center.email_account.health.status.${healthReport.status}`)
  }

  const getStatusDescription = () => {
    if (!healthReport?.status) return ""
    return i18n.t(`js.email_accounts_center.email_account.health.description.${healthReport.status}`)
  }

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case "critical":
      case "high":
        return "issues-feed-item--critical"
      case "medium":
        return "issues-feed-item--warning"
      default:
        return "issues-feed-item--notice"
    }
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "critical":
      case "high":
        return "exclamation-circle"
      case "medium":
        return "exclamation-triangle"
      default:
        return "info-circle"
    }
  }

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case "critical":
        return i18n.t("js.email_accounts_center.email_account.health.severity.critical")
      case "high":
        return i18n.t("js.email_accounts_center.email_account.health.severity.high")
      case "medium":
        return i18n.t("js.email_accounts_center.email_account.health.severity.medium")
      default:
        return i18n.t("js.email_accounts_center.email_account.health.severity.low")
    }
  }

  const getCheckItemIcon = (item: CheckItem) => {
    if (item.passed) return "check-circle"
    switch (item.severity) {
      case "critical":
      case "high":
        return "exclamation-circle"
      case "medium":
        return "exclamation-triangle"
      default:
        return "info-circle"
    }
  }

  const getCheckItemClass = (item: CheckItem) => {
    if (item.passed) return "text-success"
    switch (item.severity) {
      case "critical":
      case "high":
        return "text-danger"
      case "medium":
        return "text-warning"
      default:
        return "text-secondary"
    }
  }

  const getGroupStatusIcon = (status: string) => {
    switch (status) {
      case "critical":
      case "fail":
        return "exclamation-circle"
      case "warning":
        return "exclamation-triangle"
      default:
        return "check-circle"
    }
  }

  const getGroupStatusClass = (status: string) => {
    switch (status) {
      case "critical":
      case "fail":
        return "text-danger"
      case "warning":
        return "text-warning"
      default:
        return "text-success"
    }
  }

  const formatCategoryName = (name: string) => {
    const translationKey = `js.email_accounts_center.email_account.health.categories.${name}`
    const translation = i18n.t(translationKey)
    // If translation exists (not the key itself), use it; otherwise format the string
    if (translation !== translationKey) {
      return translation
    }
    // Fallback: capitalize and replace underscores
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, " ")
  }

  const formatIssueCount = (count: number) => {
    return i18n.t("js.email_accounts_center.email_account.health.issue_count", {
      count,
    })
  }

  const formatGroupLabel = (labelKey: string) => {
    return i18n.t(`js.email_accounts_center.email_account.health.groups.${labelKey}`)
  }

  const formatCheckItemLabel = (categoryName: string, item: CheckItem) => {
    // If passed, use the translated check name
    if (item.passed && item.key) {
      const translationKey = `js.email_accounts_center.email_account.health.checks.${categoryName}.${item.key}`
      const translation = i18n.t(translationKey)
      // If translation exists (not the key itself), use it
      if (translation !== translationKey) {
        return translation
      }
      // Fallback: format the key
      return item.key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    }
    // If failed, use the translated check name (not the error description)
    if (item.key) {
      const translationKey = `js.email_accounts_center.email_account.health.checks.${categoryName}.${item.key}`
      const translation = i18n.t(translationKey)
      if (translation !== translationKey) {
        return translation
      }
    }
    // Fallback to description if no key
    return item.description || ""
  }

  const formatDate = (timestamp: number | string | null) => {
    if (!timestamp) return ""

    let date: Date
    if (typeof timestamp === "number") {
      // Unix timestamp in seconds
      date = new Date(timestamp * 1000)
    } else {
      // Backend sends ISO 8601 format already in user's timezone
      date = new Date(timestamp)
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date:", timestamp)
      return ""
    }

    // Format the date (already in user's timezone from backend)
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="health-report mt-1 mb-10">
          <div className="health-report__score">
            <Skeleton size="freeform" style={{ width: "80px", height: "80px", borderRadius: "50%" }} />
          </div>
          <div className="health-report__content">
            <Skeleton size="freeform" style={{ width: "60%", height: "20px", borderRadius: "4px" }} className="mb-2" />
            <Skeleton size="freeform" style={{ width: "100%", height: "40px", borderRadius: "4px" }} />
          </div>
        </div>
        <Skeleton size="freeform" style={{ width: "120px", height: "20px", borderRadius: "4px" }} className="mb-4" />
        <Skeleton size="freeform" style={{ width: "100%", height: "80px", borderRadius: "4px" }} className="mb-3" />
        <Skeleton size="freeform" style={{ width: "100%", height: "80px", borderRadius: "4px" }} />
      </div>
    )
  }

  const issues = healthReport?.issues || []
  const checkGroups = healthReport?.check_groups || []
  const score = healthReport?.score ?? health

  // Check if we have any health data to display
  const hasHealthData = healthReport?.score !== null && healthReport?.score !== undefined

  // Show upgrade message for non-premium users if health data exists
  if (!isPremium && hasHealthData) {
    return (
      <div className="p-6 health-report-tab">
        <div className="empty-state">
          <div className="empty-state__img">
            <img src={emptyStateImageUrl} alt="" height="140" />
          </div>
          <div className="empty-state__content">
            <h3 className="empty-state__title">
              {i18n.t("js.email_accounts_center.email_account.health.premium_only.title")}
            </h3>
            <p className="text-balance">
              {i18n.t("js.email_accounts_center.email_account.health.premium_only.description")}
            </p>
            <div className="empty-state__actions">
              <Button size="sm" asChild>
                <a
                  href={checkout_upgrade_plan_path({
                    needed_credits: 1,
                    needed_level: 1,
                    operation: "health_report",
                    direct_route: true,
                    format: "turbo_stream",
                  })}
                  data-turbo-stream="true"
                >
                  {i18n.t("js.email_accounts_center.email_account.health.premium_only.cta")}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show empty state if no health check has been run
  if (!hasHealthData) {
    return (
      <div className="p-6 health-report-tab">
        <div className="empty-state">
          <div className="empty-state__img">
            <img src={emptyStateImageUrl} alt="" height="140" />
          </div>
          <div className="empty-state__content">
            <h3 className="empty-state__title">
              {i18n.t("js.email_accounts_center.email_account.health.no_data.title")}
            </h3>
            <p className="text-balance">
              {i18n.t("js.email_accounts_center.email_account.health.no_data.description_html")}
            </p>
            <div className="empty-state__actions">
              <Button size="sm">
                <Icon icon="shield-check" size="sm" />
                {i18n.t("js.email_accounts_center.email_account.health.no_data.cta")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`p-6 health-report-tab ${getStatusModifier()}`}>
      {/* Health report */}
      <div className="health-report mt-2 mb-10">
        <div className="health-report__score">
          <Gauge value={score ?? 0} max={100} minimal>
            <div className="text-sm-medium text-center">
              {i18n.t(`js.components.health_gauge.${getHealthLabel(score ?? 0)}.title`)}
            </div>
          </Gauge>
        </div>
        <div className="health-report__content">
          <h3 className="text-md-medium mb-2">{getStatusMessage()}</h3>
          <p className="text-xs text-body-secondary mb-0">{getStatusDescription()}</p>
        </div>
      </div>

      {/* Issues */}
      {issues.length > 0 && (
        <>
          <div className="d-flex align-items-center gap-2">
            <h3 className="text-md-medium mb-0">{i18n.t("js.email_accounts_center.email_account.health.issues")}</h3>
            <Count number={issues.length.toString()} />
          </div>
          <div className="h-hint mt-1">
            {i18n.t("js.email_accounts_center.email_account.health.issues_description")}
          </div>
          <ul className="issues-feed mt-4">
            {issues.map((issue, index) => {
              const issueTitle = issue.title || issue.description
              const issueSubtitle = issue.subtitle || issue.category

              return (
                <IssueItem
                  key={index}
                  title={issueTitle}
                  subtitle={issueSubtitle}
                  severityLabel={getSeverityLabel(issue.severity)}
                  severityIcon={getSeverityIcon(issue.severity)}
                  severityClass={getSeverityClass(issue.severity)}
                  actions={issue.actions}
                />
              )
            })}
          </ul>
        </>
      )}

      {/* Details with check groups */}
      {checkGroups.length > 0 && (
        <>
          <div className="d-flex align-items-center gap-2 mb-4 mt-10">
            <h3 className="text-md-medium mb-0">{i18n.t("js.email_accounts_center.email_account.health.details")}</h3>
            {healthReport?.last_checked_at && (
              <Hint variant="default" className="text-hint ml-auto">
                {i18n.t("js.email_accounts_center.email_account.health.last_checked", {
                  date: formatDate(healthReport.last_checked_at),
                })}
              </Hint>
            )}
          </div>
          {checkGroups.map((group) => (
            <details key={group.key} className="health-checks">
              <summary className="health-checks__header">
                <Icon icon="chevron-right" size="xs" className="text-body-secondary" />
                <h3 className="text-xs-medium mb-0 my-05">{formatGroupLabel(group.label_key)}</h3>
                <Hint variant="default" className="ml-auto">
                  {group.summary}
                </Hint>
                <Icon icon={getGroupStatusIcon(group.status)} size="sm" className={getGroupStatusClass(group.status)} />
              </summary>
              <div className="health-checks__content">
                <div className="row">
                  {group.categories.map((category) => (
                    <div key={category.name} className="col-6">
                      <div className="d-flex align-items-center gap-1 mt-2">
                        <strong className="text-xs-medium mb-0">{formatCategoryName(category.name)}</strong>
                        {category.issue_count > 0 && (
                          <Hint variant="default" className="text-hint">
                            · {formatIssueCount(category.issue_count)}
                          </Hint>
                        )}
                      </div>

                      {category.items.length > 0 ? (
                        category.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="d-flex align-items-center gap-2 mt-2">
                            <Icon icon={getCheckItemIcon(item)} size="sm" className={getCheckItemClass(item)} />
                            <span className="text-xs">{formatCheckItemLabel(category.name, item)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <Icon icon="check-circle" size="sm" className="text-success" />
                          <span className="text-xs">
                            {i18n.t("js.email_accounts_center.email_account.health.all_passed")}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </details>
          ))}
        </>
      )}
    </div>
  )
}
